import { ref, watch } from "vue";

import POSConfigModule from "@/store/modules/pos-config";
import DeviceModule from "@/store/modules/device";
import CashFloatModule from "@/store/modules/cash-float";
import {
  useFeatureFlagStore,
  ffObservability,
  FeatureFlagKeys,
  FLAGSMITH_API_URL,
  FLAGSMITH_APP_PREFIX,
} from "@/feature-flags";

const config = {
  environmentID: String(import.meta.env.VITE_FLAGSMITH_ENV_KEY),
  apiUrl: FLAGSMITH_API_URL,
  appPrefix: FLAGSMITH_APP_PREFIX,
  verbose: import.meta.env.VITE_ENV !== "prod",
  observability: ffObservability,
};

// The self-hosted Flagsmith can time out or error on init.
// Retry a few times so a transient failure at boot self-heals, defaulting to
// static endpoints meanwhile.
const MAX_INIT_ATTEMPTS = 3;
const RETRY_DELAY_MS = 5000;
const FLAGS_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const MIN_REFRESH_GAP_MS = 30 * 1000;

// Bumped after each successful fetch so flag getters recompute: resolved flags
// live in the (non-reactive) OpenFeature client, and isReady never changes on a
// same-group refetch, so nothing else would trigger reactivity.
const flagsRevision = ref(0);
function markFlagsRefreshed(): void {
  flagsRevision.value++;
}

// Flags target by group only; single source for the identify context.
const groupContext = (groupId: number) => ({
  targetingKey: String(groupId),
  groupId,
});

async function initWithRetry(groupId: number): Promise<void> {
  const store = useFeatureFlagStore();

  for (let attempt = 1; attempt <= MAX_INIT_ATTEMPTS; attempt++) {
    // Abandon if the group changed or the store became ready via another path.
    if (POSConfigModule.groupId !== groupId || store.isReady) {
      return;
    }
    try {
      await store.init({ config, context: groupContext(groupId) });
      markFlagsRefreshed();
      return;
    } catch {
      if (attempt < MAX_INIT_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }
  }
}

let refreshInFlight = false;
let lastRefreshMs = 0;

// Re-sync flags so segment changes reach an open POS without a reload.
// Guarded so overlapping focus/interval triggers cannot stack racing requests
// or resolve out of order. force=true bypasses the min-gap throttle for an
// explicit, event-driven refresh (e.g. arriving at the login screen).
export function refreshFlags(force = false): void {
  const store = useFeatureFlagStore();
  const groupId = POSConfigModule.groupId;
  const now = Date.now();

  if (
    !store.isReady ||
    !groupId ||
    refreshInFlight ||
    (!force && now - lastRefreshMs < MIN_REFRESH_GAP_MS)
  ) {
    return;
  }

  refreshInFlight = true;
  lastRefreshMs = now;
  void store
    .updateContext(groupContext(groupId))
    .then(markFlagsRefreshed)
    .catch(() => undefined)
    .finally(() => {
      refreshInFlight = false;
    });
}

let ffSetupInstalled = false;

// POS targets flags by group only, and groupId is known only after login.
// Init when it first resolves, re-identify on group switches, fail closed.
export function setupFeatureFlags(): void {
  if (ffSetupInstalled) {
    return;
  }
  ffSetupInstalled = true;

  watch(
    () => POSConfigModule.groupId,
    (groupId) => {
      if (!groupId) {
        return;
      }

      const store = useFeatureFlagStore();

      if (store.isReady) {
        void store
          .updateContext(groupContext(groupId))
          .then(markFlagsRefreshed)
          .catch(() => undefined);
      } else {
        void initWithRetry(groupId);
      }
    },
    // immediate so a reload, where groupId is already restored from
    // localStorage before this watcher registers, still initializes.
    { immediate: true }
  );

  // POS terminals can stay open for days, keep flags fresh in the background
  // and re-sync when the window regains focus. Wrapped so the focus Event is
  // not passed as the force argument.
  setInterval(() => refreshFlags(), FLAGS_REFRESH_INTERVAL_MS);
  window.addEventListener("focus", () => refreshFlags());
}

// Cashbook v2 rollout - hardcoded per environment rather than Flagsmith:
// Flagsmith init can fail to become ready (e.g. self-hosted instance
// timeout) and leave this permanently off for everyone in that session.
const CASHBOOK_V2_GROUP_IDS: Record<string, number[]> = {
  dev: [19545, 16383, 16441, 16416, 21423],
  test: [19545, 16383, 16441, 16416, 19693, 19546],
  prod: [19693, 19066, 19546],
};

export function isCashbookV2Enabled(): boolean {
  const env = import.meta.env.VITE_ENV;
  const allowlist = CASHBOOK_V2_GROUP_IDS[env] || CASHBOOK_V2_GROUP_IDS.prod;
  const groupId = POSConfigModule.groupId;
  return groupId != null && allowlist.includes(groupId);
}

// The opening/closing/transfer flows assume a device linked to a cash
// location, so they fall back to the legacy V1 UI when unlinked - even
// though the rest of the V2 experience (e.g. the "sales closed" banner)
// still shows for an eligible group.
export function isCashbookV2FlowEnabled(): boolean {
  return isCashbookV2Enabled() && !!DeviceModule.hardwareLocation;
}

// Per-group exemption from the FIS-42 web POS block: POS flags are identified
// by groupId, so enabling pos::web-pos-fiscal-exemption for a segment lets
// that group keep using the web POS regardless of its country (e.g. demos).
// Fails closed — no exemption while Flagsmith is unreachable or the flag is
// absent — so an outage can never silently re-enable non-compliant web sales.
export function isWebPosFiscalExempt(): boolean {
  const store = useFeatureFlagStore();
  // Track the revision so callers recompute when a refresh changes flag values.
  void flagsRevision.value;

  const exempt =
    store.isReady && store.isEnabled(FeatureFlagKeys.WebPosFiscalExemption);
  console.log("[FIS-42] isWebPosFiscalExempt", {
    flagsmithReady: store.isReady,
    flagEnabledForGroup: store.isReady
      ? store.isEnabled(FeatureFlagKeys.WebPosFiscalExemption)
      : "(store not ready)",
    groupId: POSConfigModule.groupId,
    exempt,
  });
  return exempt;
}

// FIS-529 rollout gate: print receipts through @smeetz/receipt instead of the
// legacy template. Fails closed — legacy prints while Flagsmith is unreachable
// or the flag is absent — so an outage can never change what a till prints.
export function isUnifiedReceiptEnabled(): boolean {
  const store = useFeatureFlagStore();
  // Track the revision so callers recompute when a refresh changes flag values.
  void flagsRevision.value;
  return store.isReady && store.isEnabled(FeatureFlagKeys.UnifiedReceipt);
}

// PIN login needs only groupId: from the selected config, or from
// an open cashfloat session (which implies a config was already loaded).
export function pinTerminalGroupId(): number | null {
  return (
    POSConfigModule.selectedConfig?.group ??
    CashFloatModule.cashFloat?.groupId ??
    null
  );
}

// Rollout gate shared by the login screen and the navbar lock shortcut: enable
// PIN login only for a known group with the flag enabled.
export function isPinLoginEnabled(): boolean {
  const store = useFeatureFlagStore();
  // Track the revision so this recomputes when a refresh changes flag values.
  void flagsRevision.value;

  return (
    !!pinTerminalGroupId() &&
    store.isReady &&
    store.isEnabled(FeatureFlagKeys.PosSellerPin)
  );
}
